import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Button,
  Paper,
  CircularProgress,
  Alert,
} from '@mui/material';
import { FileDownload, Print, Refresh } from '@mui/icons-material';
import { notificationApi } from '../services/api';

interface AlertLog {
  id: number;
  title: string;
  message: string;
  severity: 'info' | 'warning' | 'error' | 'critical';
  is_read: boolean;
  created_at: string;
  farm_id: number;
  user_id?: number;
  type: string;
}

interface NotificationStats {
  total_count: number;
  unread_count: number;
  critical_count: number;
  warning_count: number;
  error_count: number;
  info_count: number;
}

const AlertsPage: React.FC = () => {
  const [alertLogs, setAlertLogs] = useState<AlertLog[]>([]);
  const [stats, setStats] = useState<NotificationStats>({
    total_count: 0,
    unread_count: 0,
    critical_count: 0,
    warning_count: 0,
    error_count: 0,
    info_count: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      setError(null);

      // 강제로 토큰을 가져와서 axios에 직접 추가
      const { TokenManager } = await import('../utils/tokenManager');
      TokenManager.initialize();

      const token = await TokenManager.ensureValidToken();
      console.log('🔍 AlertsPage Direct Token:', {
        token: token ? `${token.substring(0, 20)}...` : 'null',
        isLoggedIn: TokenManager.isLoggedIn()
      });

      if (!token) {
        throw new Error('No authentication token available');
      }

      // axios 인스턴스를 직접 사용하면서 헤더 강제 추가
      const { default: api } = await import('../services/api');

      const [notificationsResponse, statsResponse] = await Promise.all([
        api.get('/notifications', {
          headers: { Authorization: `Bearer ${token}` }
        }),
        api.get('/notifications/stats/summary', {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      // Ensure response data is an array
      const notifications = Array.isArray(notificationsResponse.data)
        ? notificationsResponse.data
        : [];

      setAlertLogs(notifications);
      setStats(statsResponse.data || {
        total_count: 0,
        unread_count: 0,
        critical_count: 0,
        warning_count: 0,
        error_count: 0,
        info_count: 0
      });
    } catch (error: any) {
      console.error('Failed to load notifications:', error);
      
      // 인증 에러인 경우 특별 처리
      if (error.response?.status === 403 || error.response?.status === 401) {
        setError('로그인이 필요합니다. 로그인 페이지로 이동해주세요.');
        // 인증 에러 시 로그인 페이지로 리다이렉트할 수 있음
        // window.location.href = '/login';
      } else {
        setError('알림 데이터를 불러오는데 실패했습니다.');
      }
      
      // Set empty array on error to prevent map error
      setAlertLogs([]);
      // Set default stats on error
      setStats({
        total_count: 0,
        unread_count: 0,
        critical_count: 0,
        warning_count: 0,
        error_count: 0,
        info_count: 0
      });
    } finally {
      setLoading(false);
    }
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const handleRefresh = () => {
    loadNotifications();
  };

  const handleExportCSV = () => {
    const csvContent = [
      ['Timestamp', 'Title', 'Message', 'Severity'],
      ...alertLogs.map(log => [
        formatTimestamp(log.created_at),
        log.title,
        log.message,
        getChipLabel(log.severity)
      ])
    ]
    .map(row => row.map(field => `"${field}"`).join(','))
    .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `notifications_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const handlePrint = () => {
    window.print();
  };

  useEffect(() => {
    loadNotifications();
  }, []);

  const getChipColor = (severity: string) => {
    switch (severity) {
      case 'critical':
        return 'error';
      case 'error':
        return 'error';
      case 'warning':
        return 'warning';
      case 'info':
        return 'info';
      default:
        return 'default';
    }
  };

  const getChipLabel = (severity: string) => {
    switch (severity) {
      case 'critical':
        return '심각';
      case 'error':
        return '오류';
      case 'warning':
        return '경고';
      case 'info':
        return '정보';
      default:
        return '알 수 없음';
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
          오류 로그 & 알람 기록
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={handleRefresh}
            disabled={loading}
            sx={{ borderRadius: 2 }}
          >
            새로고침
          </Button>
          <Button
            variant="outlined"
            startIcon={<FileDownload />}
            onClick={handleExportCSV}
            disabled={loading || alertLogs.length === 0}
            sx={{ borderRadius: 2 }}
          >
            CSV 내보내기
          </Button>
          <Button
            variant="outlined"
            startIcon={<Print />}
            onClick={handlePrint}
            disabled={loading}
            sx={{ borderRadius: 2 }}
          >
            출력
          </Button>
        </Box>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {/* Alert Logs Table */}
      <Card sx={{ borderRadius: 3 }}>
        <CardContent sx={{ p: 0 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper} sx={{ borderRadius: 3 }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 'bold', py: 2 }}>
                      Time stamp
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', py: 2 }}>
                      제목
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', py: 2 }}>
                      메시지
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', py: 2, textAlign: 'center' }}>
                      심각도
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {alertLogs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} sx={{ textAlign: 'center', py: 4 }}>
                        <Typography color="text.secondary">
                          알림이 없습니다.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    alertLogs.map((log) => (
                      <TableRow
                        key={log.id}
                        sx={{
                          '&:hover': {
                            bgcolor: 'grey.50',
                          },
                          opacity: log.is_read ? 0.7 : 1,
                        }}
                      >
                        <TableCell sx={{ py: 2, fontFamily: 'monospace' }}>
                          {formatTimestamp(log.created_at)}
                        </TableCell>
                        <TableCell sx={{ py: 2, fontWeight: log.is_read ? 'normal' : 'bold' }}>
                          {log.title}
                        </TableCell>
                        <TableCell sx={{ py: 2 }}>
                          {log.message}
                        </TableCell>
                        <TableCell sx={{ py: 2, textAlign: 'center' }}>
                          <Chip
                            label={getChipLabel(log.severity)}
                            color={getChipColor(log.severity) as any}
                            size="small"
                            sx={{
                              fontWeight: 'bold',
                              minWidth: 60,
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Summary Card */}
      <Card sx={{ mt: 3, borderRadius: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
            알림 요약
          </Typography>
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="body2" color="text.secondary">
                총 알림 수
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                {loading ? '...' : stats.total_count}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                읽지 않은 알림
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                {loading ? '...' : stats.unread_count}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                심각 알림
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                {loading ? '...' : stats.critical_count}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                경고 알림
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'warning.main' }}>
                {loading ? '...' : stats.warning_count}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                오류 알림
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                {loading ? '...' : stats.error_count}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                정보 알림
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'info.main' }}>
                {loading ? '...' : stats.info_count}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default AlertsPage;